import React from "react";
import { Marker, InfoWindow } from "@react-google-maps/api";
import { Review, GroupedReview } from "@/types/review";
import { getMarkerIconColor } from "./MarkerUtils";
import { useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { Star, Bed, Bath, Home, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";

interface ReviewMarkersProps {
  reviews: Review[];
  groupedReviews?: GroupedReview[];
}

const ReviewMarkers: React.FC<ReviewMarkersProps> = ({ reviews, groupedReviews }) => {
  const [selectedMarker, setSelectedMarker] = useState<Review | GroupedReview | null>(null);
  const { t } = useLanguage();
  
  // Translate housing type
  const translateHousingType = (type: string): string => {
    return t(`form.housingTypes.${type}`) || type;
  };

  const getRatingColor = (rating: number): string => {
    if (rating <= 2) return 'text-rating-low';
    if (rating === 3) return 'text-rating-mid';
    return 'text-rating-high';
  };
  
  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };
  
  const renderMarkers = () => {
    if (groupedReviews && groupedReviews.length > 0) {
      return groupedReviews.map((groupedReview) => {
        // Ensure coordinates are valid numbers
        const lat = parseFloat(String(groupedReview.coordinates.lat));
        const lng = parseFloat(String(groupedReview.coordinates.lng));
        
        // Skip invalid coordinates
        if (isNaN(lat) || isNaN(lng)) return null;
        
        return (
          <Marker 
            key={`grouped-${groupedReview.locationKey}`}
            position={{
              lat: lat,
              lng: lng
            }}
            icon={{
              path: google.maps.SymbolPath.CIRCLE,
              fillColor: getMarkerIconColor(groupedReview.averageRating),
              fillOpacity: 1,
              strokeWeight: 2,
              strokeColor: '#ffffff',
              scale: 12,
            }}
            label={{
              text: groupedReview.totalReviews.toString(),
              color: '#ffffff',
              fontSize: '10px',
              fontWeight: 'bold'
            }}
            onClick={() => setSelectedMarker(groupedReview)}
          />
        );
      });
    }
    
    // Filter reviews with valid coordinates
    const validReviews = reviews.filter((review) => {
      if (!review.coordinates) return false;
      
      const lat = parseFloat(String(review.coordinates.lat));
      const lng = parseFloat(String(review.coordinates.lng));
      
      return !isNaN(lat) && !isNaN(lng);
    });
    
    return validReviews.map((review) => {
      const lat = parseFloat(String(review.coordinates.lat));
      const lng = parseFloat(String(review.coordinates.lng));
      
      return (
        <Marker 
          key={`review-${review.id}`} 
          position={{
            lat: lat,
            lng: lng
          }}
          icon={{
            path: google.maps.SymbolPath.CIRCLE,
            fillColor: getMarkerIconColor(review.rating),
            fillOpacity: 1,
            strokeWeight: 2,
            strokeColor: '#ffffff',
            scale: 10,
          }}
          onClick={() => setSelectedMarker(review)}
        />
      );
    });
  };

  return (
    <>
      {renderMarkers()}
      
      {selectedMarker && 'address' in selectedMarker ? (
        <InfoWindow
          position={{
            lat: parseFloat(String(selectedMarker.coordinates.lat)),
            lng: parseFloat(String(selectedMarker.coordinates.lng))
          }}
          onCloseClick={() => setSelectedMarker(null)}
          options={{ maxWidth: 250 }}
        >
          <div className="p-2 max-w-[230px]">
            <div className="mb-1.5">
              <h3 className="font-medium text-xs mb-1 line-clamp-1">
                {selectedMarker.communityName || selectedMarker.address}
              </h3>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-0.5">
                  <div className={`flex items-center gap-0.5 ${getRatingColor(selectedMarker.averageRating)}`}>
                    <Star className="h-3 w-3 fill-current" />
                    <span className="text-xs font-medium">{selectedMarker.averageRating.toFixed(1)}</span>
                  </div>
                </div>
                <div className="text-[10px] text-muted-foreground">
                  {t('totalReviews', { count: selectedMarker.totalReviews })}
                </div>
              </div>
              
              {selectedMarker.lastReviewDate && (
                <div className="flex items-center gap-0.5 text-[10px] text-muted-foreground">
                  <Calendar className="h-2.5 w-2.5" />
                  <span>{t('latestReview')}: {formatDate(selectedMarker.lastReviewDate)}</span>
                </div>
              )}
            </div>
            
            <div className="space-y-1.5 mb-1.5">
              <div className="flex flex-wrap gap-1.5">
                {selectedMarker.minBedrooms > 0 && (
                  <div className="flex items-center gap-0.5 text-[10px]">
                    <Bed className="h-2.5 w-2.5 text-muted-foreground" />
                    <span>{selectedMarker.minBedrooms === selectedMarker.maxBedrooms ? 
                      selectedMarker.minBedrooms : `${selectedMarker.minBedrooms}-${selectedMarker.maxBedrooms}`}</span>
                  </div>
                )}
                
                {selectedMarker.minBathrooms > 0 && (
                  <div className="flex items-center gap-0.5 text-[10px]">
                    <Bath className="h-2.5 w-2.5 text-muted-foreground" />
                    <span>{selectedMarker.minBathrooms === selectedMarker.maxBathrooms ? 
                      selectedMarker.minBathrooms : `${selectedMarker.minBathrooms}-${selectedMarker.maxBathrooms}`}</span>
                  </div>
                )}
                
                {selectedMarker.housingTypes && selectedMarker.housingTypes.length > 0 && (
                  <div className="flex items-center gap-0.5 text-[10px]">
                    <Home className="h-2.5 w-2.5 text-muted-foreground" />
                    <span className="line-clamp-1">
                      {selectedMarker.housingTypes.map(type => translateHousingType(type)).join(', ')}
                    </span>
                  </div>
                )}
              </div>
              
              <div className="flex flex-wrap gap-1">
                {selectedMarker.hasImmigrantFriendly && (
                  <Badge variant="outline" className="rounded-full px-1 py-0 text-[10px] bg-gray-100">
                    {t('immigrantFriendly')}
                  </Badge>
                )}
                {selectedMarker.hasPetAllowed && (
                  <Badge variant="outline" className="rounded-full px-1 py-0 text-[10px] bg-gray-100">
                    {t('petAllowed')}
                  </Badge>
                )}
              </div>
            </div>
            
            {selectedMarker.minRent > 0 && (
              <div className="text-[10px] font-medium mb-1.5">
                {t('rentRange')}: {t('currency')} {selectedMarker.minRent.toLocaleString('en-CA')}
                {selectedMarker.minRent !== selectedMarker.maxRent && 
                  ` - ${selectedMarker.maxRent.toLocaleString('en-CA')}`}
              </div>
            )}
            
            <Button asChild size="sm" className="w-full h-6 text-[10px] px-2">
              <Link to={`/address/${encodeURIComponent(selectedMarker.address)}`}>
                {t('viewDetails')}
              </Link>
            </Button>
          </div>
        </InfoWindow>
      ) : selectedMarker && (
        <InfoWindow
          position={{
            lat: parseFloat(String(selectedMarker.coordinates.lat)),
            lng: parseFloat(String(selectedMarker.coordinates.lng))
          }}
          onCloseClick={() => setSelectedMarker(null)}
          options={{ maxWidth: 250 }}
        >
          <div className="p-2 max-w-[230px]">
            <div className="mb-1.5">
              <h3 className="font-medium text-xs mb-1 line-clamp-1">
                {(selectedMarker as Review).community_name || (selectedMarker as Review).address_detail}
              </h3>
              <div className="flex items-center justify-between mb-1">
                <div className={`flex items-center gap-0.5 ${getRatingColor((selectedMarker as Review).rating)}`}>
                  <Star className="h-3 w-3 fill-current" />
                  <span className="text-xs font-medium">{(selectedMarker as Review).rating.toFixed(1)}</span>
                </div>
                <div className="text-[10px] text-muted-foreground">
                  {t('singleReview')}
                </div>
              </div>
              
              <div className="flex items-center gap-0.5 text-[10px] text-muted-foreground">
                <Calendar className="h-2.5 w-2.5" />
                <span>{t('latestReview')}: {formatDate((selectedMarker as Review).created_at)}</span>
              </div>
            </div>
            
            <div className="space-y-1.5 mb-1.5">
              <div className="flex flex-wrap gap-1.5">
                {(selectedMarker as Review).bedrooms > 0 && (
                  <div className="flex items-center gap-0.5 text-[10px]">
                    <Bed className="h-2.5 w-2.5 text-muted-foreground" />
                    <span>{(selectedMarker as Review).bedrooms}</span>
                  </div>
                )}
                
                {(selectedMarker as Review).bathrooms > 0 && (
                  <div className="flex items-center gap-0.5 text-[10px]">
                    <Bath className="h-2.5 w-2.5 text-muted-foreground" />
                    <span>{(selectedMarker as Review).bathrooms}</span>
                  </div>
                )}
                
                {(selectedMarker as Review).housing_type && (
                  <div className="flex items-center gap-0.5 text-[10px]">
                    <Home className="h-2.5 w-2.5 text-muted-foreground" />
                    <span className="line-clamp-1">
                      {translateHousingType((selectedMarker as Review).housing_type || "")}
                    </span>
                  </div>
                )}
              </div>
              
              <div className="flex flex-wrap gap-1">
                {(selectedMarker as Review).immigrant_friendly && (
                  <Badge variant="outline" className="rounded-full px-1 py-0 text-[10px] bg-gray-100">
                    {t('immigrantFriendly')}
                  </Badge>
                )}
                {(selectedMarker as Review).pet_allowed && (
                  <Badge variant="outline" className="rounded-full px-1 py-0 text-[10px] bg-gray-100">
                    {t('petAllowed')}
                  </Badge>
                )}
              </div>
            </div>
            
            {(selectedMarker as Review).rent && (
              <div className="text-[10px] font-medium mb-1.5">
                {t('rent')}: {t('currency')} {Number((selectedMarker as Review).rent).toLocaleString('en-CA')}
              </div>
            )}
            
            <Button asChild size="sm" className="w-full h-6 text-[10px] px-2">
              <Link to={`/address/${encodeURIComponent((selectedMarker as Review).address_detail)}`}>
                {t('viewDetails')}
              </Link>
            </Button>
          </div>
        </InfoWindow>
      )}
    </>
  );
};

export default ReviewMarkers;
